import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Stack,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";

import { useEmpresa } from "../../../../app/providers/EmpresaProvider";
import AttendanceDayDetailModal from "./AttendanceDayDetailModal";
import { getAttendanceEmployeeDayRecords } from "../api/asistenciaApi";
import {
  calculateTotalMinutesInRange,
  formatHoursLabel,
} from "../utils/periodHelpers";

const HOURS_COLOR = "#0284C7";
const ZERO_HOURS_COLOR = "rgba(0,0,0,0.5)";

function pad2(n) {
  return String(n).padStart(2, "0");
}

function fmtYMD(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function parseYMD(value) {
  return new Date(`${String(value).slice(0, 10)}T00:00:00`);
}

function startOfWeekMonday(date) {
  const next = new Date(date);
  const day = next.getDay();
  const diff = day === 0 ? -6 : 1 - day;

  next.setDate(next.getDate() + diff);
  next.setHours(0, 0, 0, 0);

  return next;
}

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function endOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

function getPresetRange(preset) {
  const today = new Date();

  if (preset === "MES_ACTUAL") {
    return {
      from: fmtYMD(startOfMonth(today)),
      to: fmtYMD(today),
    };
  }

  if (preset === "MES_ANTERIOR") {
    const previous = new Date(today.getFullYear(), today.getMonth() - 1, 1);

    return {
      from: fmtYMD(startOfMonth(previous)),
      to: fmtYMD(endOfMonth(previous)),
    };
  }

  return {
    from: fmtYMD(startOfWeekMonday(today)),
    to: fmtYMD(today),
  };
}

function getDayOfWeek(dateString) {
  const days = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

  try {
    const date = parseYMD(dateString);
    return days[date.getDay()];
  } catch {
    return "";
  }
}

function formatDateShort(dateString) {
  if (!dateString) return "-";

  const [year, month, day] = String(dateString).slice(0, 10).split("-");

  if (!year || !month || !day) return dateString;

  return `${day}/${month}`;
}

function formatTimeDisplay(value) {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isFinite(date.getTime())) {
    return new Intl.DateTimeFormat("es-AR", {
      timeZone: "America/Argentina/Buenos_Aires",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(date);
  }

  const timeStr = String(value).trim();

  if (timeStr.length >= 5 && timeStr.includes(":")) {
    return timeStr.slice(0, 5);
  }

  return "-";
}

function formatWorkedLabel(minutes) {
  const total = Math.max(0, Number(minutes) || 0);
  const hours = Math.floor(total / 60);
  const mins = total % 60;

  return `${hours}hs ${String(mins).padStart(2, "0")}m`;
}

function getStatusLabel(row) {
  const status = String(row?.latestStatus || row?.status || "").toUpperCase();

  if (status === "OPEN") return "En jornada";
  if (status === "CLOSED") return "Finalizado";
  if (status === "ABSENT") return "Ausencia";
  if (status === "FRANCO") return "Franco";

  return status || "-";
}

function getStatusStyle(row) {
  const status = String(row?.latestStatus || row?.status || "").toUpperCase();

  if (status === "OPEN") {
    return {
      bgcolor: "rgba(33, 150, 243, 0.12)",
      color: "#0B67B2",
      border: "2px solid rgba(33, 150, 243, 0.55)",
    };
  }

  if (status === "CLOSED") {
    return {
      bgcolor: "rgba(76, 175, 80, 0.15)",
      color: "#15803D",
      border: "2px solid rgba(76, 175, 80, 0.55)",
    };
  }

  if (status === "ABSENT") {
    return {
      bgcolor: "rgba(244, 67, 54, 0.12)",
      color: "#D32F2F",
      border: "2px solid rgba(244, 67, 54, 0.55)",
    };
  }

  return {
    bgcolor: "rgba(117, 117, 138, 0.16)",
    color: "#4B5563",
    border: "2px solid rgba(117, 117, 138, 0.35)",
  };
}

function PeriodFilters({
  fromDate,
  toDate,
  preset,
  onFromChange,
  onToChange,
  onPresetChange,
  onApply,
}) {
  const presetButtons = [
    { key: "SEMANA_ACTUAL", label: "Semana actual" },
    { key: "MES_ACTUAL", label: "Mes actual" },
    { key: "MES_ANTERIOR", label: "Mes anterior" },
  ];

  const inputSx = {
    width: 160,
    "& .MuiOutlinedInput-root": {
      height: 30,
      bgcolor: "#AFBAD9",
      borderRadius: "8px",
      border: "3px solid rgba(255,255,255,0.55)",
      fontSize: 12,
      fontWeight: 700,
      color: "primary.900",
      "& fieldset": { border: "none" },
      "& input": {
        py: 0,
        px: 0.5,
        fontSize: 12,
        fontWeight: 700,
      },
    },
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{
        width: "100%",
        bgcolor: "#FFFFFF",
        borderRadius: "10px",
        px: 1,
        py: 1,
        boxShadow: "inset 0px 0px 2px rgba(96,96,113,0.18)",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: "primary.900" }}>
          Desde
        </Typography>

        <TextField
          size="small"
          type="date"
          value={fromDate}
          onChange={(e) => onFromChange(e.target.value)}
          sx={inputSx}
        />

        <Typography sx={{ fontSize: 14, fontWeight: 700, color: "primary.900", ml: 1 }}>
          Hasta
        </Typography>

        <TextField
          size="small"
          type="date"
          value={toDate}
          onChange={(e) => onToChange(e.target.value)}
          sx={inputSx}
        />

        <Button
          onClick={onApply}
          size="small"
          sx={{
            ml: 0.5,
            height: 30,
            minWidth: 88,
            px: 2,
            bgcolor: "#7F93CE",
            color: "logoblanco",
            fontWeight: 900,
            borderRadius: "8px",
            textTransform: "none",
            fontSize: 12,
            "&:hover": { bgcolor: "secondary.600" },
          }}
        >
          Aplicar
        </Button>
      </Stack>

      <Box sx={{ flex: 1 }} />

      <Stack direction="row" spacing={1}>
        {presetButtons.map((button) => {
          const active = preset === button.key;

          return (
            <Button
              key={button.key}
              onClick={() => onPresetChange(button.key)}
              size="small"
              sx={{
                height: 30,
                minWidth: 120,
                px: 1.4,
                borderRadius: "8px",
                bgcolor: active ? "secondary.500" : "#7F93CE",
                color: "logoblanco",
                textTransform: "none",
                fontWeight: 900,
                fontSize: 12,
                boxShadow: active ? "inset 0px 0px 5px rgba(0,0,0,0.22)" : "none",
                "&:hover": {
                  bgcolor: active ? "secondary.600" : "secondary.400",
                },
              }}
            >
              {button.label}
            </Button>
          );
        })}
      </Stack>
    </Stack>
  );
}

function AttendanceDetailTable({ rows = [], onRowClick }) {
  return (
    <TableContainer
      sx={{
        bgcolor: "transparent",
        borderRadius: "14px",
        overflow: "auto",
      }}
    >
      <Table stickyHeader sx={{ width: "100%" }}>
        <TableHead>
          <TableRow
            sx={{
              "& .MuiTableCell-head": {
                bgcolor: "#606071",
                color: "#FFFFFF",
                fontWeight: 900,
                fontSize: 14,
                py: 1,
                px: 2,
                textAlign: "center",
                borderBottom: "none",
              },
              "& .MuiTableCell-head:first-of-type": {
                borderTopLeftRadius: "10px",
              },
              "& .MuiTableCell-head:last-of-type": {
                borderTopRightRadius: "10px",
              },
            }}
          >
            <TableCell align="center">Fecha</TableCell>
            <TableCell align="center">Día</TableCell>
            <TableCell align="center">Entrada</TableCell>
            <TableCell align="center">Salida</TableCell>
            <TableCell align="center">Horas</TableCell>
            <TableCell align="center">Estado</TableCell>
            <TableCell align="center">Área</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} align="center" sx={{ py: 3, bgcolor: "#FFFFFF" }}>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "neutral.700",
                  }}
                >
                  No hay registros de asistencia
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row, index) => {
              const fecha = row.ymd || row.date || row.workDate || "";
              const entrada = formatTimeDisplay(row.firstClockInAt || row.checkInTime || row.entrada);
              const salida = formatTimeDisplay(row.lastClockOutAt || row.checkOutTime || row.salida);
              const horas = formatWorkedLabel(row.workedMinutes || 0);
              const workedMinutes = Number(row.workedMinutes || 0);
              const hoursColor = workedMinutes > 0 ? HOURS_COLOR : ZERO_HOURS_COLOR;
              const estado = getStatusLabel(row);
              const statusStyle = getStatusStyle(row);
              const area = row.activeAreaName || row.areaName || row.area || "-";

              return (
                <TableRow
                  key={`${fecha}-${index}`}
                  onClick={() => onRowClick?.(row)}
                  sx={{
                    bgcolor: "#FFFFFF",
                    borderBottom: "1px solid rgba(96, 96, 113, 0.16)",
                    cursor: "pointer",
                    transition: "background-color 0.15s ease, transform 0.15s ease",
                    "&:hover": {
                      bgcolor: "rgba(175, 186, 217, 0.18)",
                    },
                    "& .MuiTableCell-body": {
                      py: 1,
                      px: 2,
                      fontSize: 12,
                      fontWeight: 700,
                      textAlign: "center",
                    },
                  }}
                >
                  <TableCell align="center">{formatDateShort(fecha)}</TableCell>

                  <TableCell align="center">
                    <Typography sx={{ fontWeight: 700, fontSize: 12 }}>
                      {getDayOfWeek(fecha)}
                    </Typography>
                  </TableCell>

                  <TableCell align="center">{entrada}</TableCell>

                  <TableCell align="center">{salida}</TableCell>

                  <TableCell align="center">
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: 800,
                        color: hoursColor,
                      }}
                    >
                      {horas}
                    </Typography>
                  </TableCell>

                  <TableCell align="center">
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: 92,
                        px: 1.2,
                        py: 0.35,
                        borderRadius: "7px",
                        fontSize: 12,
                        fontWeight: 900,
                        ...statusStyle,
                      }}
                    >
                      {estado}
                    </Box>
                  </TableCell>

                  <TableCell align="center">
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: "primary.900",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: 170,
                        mx: "auto",
                      }}
                      title={area}
                    >
                      {area}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function AttendanceEmployeeDetail({ employee }) {
  const { empresaId, sucursalId } = useEmpresa();

  const defaultRange = useMemo(() => getPresetRange("SEMANA_ACTUAL"), []);

  const [detailRows, setDetailRows] = useState([]);
  const [detailMeta, setDetailMeta] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [selectedDayRow, setSelectedDayRow] = useState(null);

  const [fromDate, setFromDate] = useState(defaultRange.from);
  const [toDate, setToDate] = useState(defaultRange.to);
  const [appliedFromDate, setAppliedFromDate] = useState(defaultRange.from);
  const [appliedToDate, setAppliedToDate] = useState(defaultRange.to);
  const [preset, setPreset] = useState("SEMANA_ACTUAL");

  const employeeId = Number(employee?.employeeId || employee?.id || 0);

  const employeeName =
    employee?.fullName ||
    employee?.nombre ||
    detailRows?.[0]?.fullName ||
    `Empleado #${employee?.employeeId || employee?.id}`;

  const periodTotalMinutes = useMemo(
    () => calculateTotalMinutesInRange(detailRows),
    [detailRows]
  );

  const visibleRows = useMemo(() => {
    return Array.isArray(detailRows) ? detailRows : [];
  }, [detailRows]);

  const loadAttendanceSnapshot = useCallback(
    async (opts = {}) => {
      if (!employeeId) return;

      try {
        setLoadingDetail(true);

        const records = await getAttendanceEmployeeDayRecords(
          {
            licenseId: empresaId || undefined,
            branchId: sucursalId || undefined,
            employeeId,
            fromDate: appliedFromDate,
            toDate: appliedToDate,
            page: 1,
            limit: 100,
          },
          { signal: opts.signal }
        );

        const recordsData = Array.isArray(records?.data) ? records.data : [];

        setDetailRows(recordsData);
        setDetailMeta(records?.meta || null);
      } catch (err) {
        if (err?.name !== "AbortError") {
          console.error("attendance employee detail error:", err);
          setDetailRows([]);
          setDetailMeta(null);
        }
      } finally {
        setLoadingDetail(false);
      }
    },
    [employeeId, empresaId, sucursalId, appliedFromDate, appliedToDate]
  );

  useEffect(() => {
    if (!employeeId) return undefined;

    const ac = new AbortController();

    loadAttendanceSnapshot({ signal: ac.signal });

    return () => ac.abort();
  }, [employeeId, loadAttendanceSnapshot]);

  const handlePresetChange = (presetKey) => {
    const range = getPresetRange(presetKey);

    setPreset(presetKey);
    setFromDate(range.from);
    setToDate(range.to);
    setAppliedFromDate(range.from);
    setAppliedToDate(range.to);
  };

  const handleFromChange = (value) => {
    setFromDate(value);
    setPreset("");
  };

  const handleToChange = (value) => {
    setToDate(value);
    setPreset("");
  };

  const handleApplyPeriod = () => {
    if (!fromDate || !toDate) return;

    const from = fromDate <= toDate ? fromDate : toDate;
    const to = fromDate <= toDate ? toDate : fromDate;

    setFromDate(from);
    setToDate(to);
    setAppliedFromDate(from);
    setAppliedToDate(to);
    setPreset("");
  };

  const totalHoursColor =
    Number(periodTotalMinutes || 0) > 0 ? HOURS_COLOR : ZERO_HOURS_COLOR;

  return (
    <>
      <Box
        sx={{
          bgcolor: "#A5A5B2",
          p: 2,
          borderRadius: "30px",
          width: "100%",
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Stack spacing={1} sx={{ flex: 1, minHeight: 0 }}>
          <Typography
            sx={{
              bgcolor: "#FFFFFF",
              fontSize: 24,
              fontWeight: 900,
              borderRadius: "12px",
              width: "100%",
              px: 1,
              py: 1,
              color: "primary.900",
              boxShadow: "inset 0px 0px 2px rgba(96,96,113,0.18)",
            }}
          >
            {employeeName}
          </Typography>

          <PeriodFilters
            fromDate={fromDate}
            toDate={toDate}
            preset={preset}
            onFromChange={handleFromChange}
            onToChange={handleToChange}
            onPresetChange={handlePresetChange}
            onApply={handleApplyPeriod}
          />

          <Typography
            component="div"
            sx={{
              bgcolor: "#FFFFFF",
              fontSize: 14,
              fontWeight: 700,
              borderRadius: "8px",
              px: 1,
              py: 1.5,
              color: "primary.900",
              boxShadow: "inset 0px 0px 2px rgba(96,96,113,0.16)",
            }}
          >
            Total de horas realizadas:{" "}
            <Box
              component="span"
              sx={{
                fontWeight: 950,
                color: totalHoursColor,
              }}
            >
              {formatHoursLabel(periodTotalMinutes)}
            </Box>
          </Typography>

          <Box
            sx={{
              bgcolor: "#D8D8DE",
              borderRadius: "24px",
              boxShadow:
                "inset 0px 0px 2px rgba(96, 96, 113, 0.22), 0px 8px 18px rgba(0,0,0,0.04)",
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                overflowX: "auto",
                scrollbarWidth: "thin",
                scrollbarColor: "rgba(17,24,39,.5) transparent",
                "&::-webkit-scrollbar": { width: "10px" },
                "&::-webkit-scrollbar-track": { background: "transparent" },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(17,24,39,.25)",
                  borderRadius: "999px",
                  border: "3px solid transparent",
                  backgroundClip: "content-box",
                },
              }}
            >
              <AttendanceDetailTable
                rows={visibleRows}
                onRowClick={(row) => setSelectedDayRow(row)}
              />
            </Box>

            {loadingDetail ? (
              <Typography
                sx={{
                  px: 1,
                  py: 0.75,
                  fontSize: 12,
                  fontWeight: 700,
                  color: "neutral.700",
                }}
              >
                Cargando asistencia...
              </Typography>
            ) : null}

            {detailMeta?.hasNextPage ? (
              <Typography
                sx={{
                  px: 1,
                  py: 0.75,
                  fontSize: 12,
                  fontWeight: 700,
                  color: "neutral.700",
                }}
              >
                Hay más registros para este período. Si necesitás ver más de 100 días,
                ampliamos la paginación.
              </Typography>
            ) : null}
          </Box>
        </Stack>
      </Box>

      <AttendanceDayDetailModal
        open={!!selectedDayRow}
        row={selectedDayRow}
        employeeName={employeeName}
        onClose={() => setSelectedDayRow(null)}
        onSaved={() => {
          setSelectedDayRow(null);
          loadAttendanceSnapshot();
        }}
      />
    </>
  );
}